import React from "react";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { RfmSegment } from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

type Props = {
    segments: RfmSegment[];
};

type SegmentMeta = {
    color: string;
    description: string;
};

// Color is a secondary cue; the segment name + description always carry meaning.
// Wording is cadence-based: the typical account remits ~once a month.
const SEGMENT_META: Record<string, SegmentMeta> = {
    Champions: { color: "var(--vb-success-500)", description: "Sends well above monthly, just now" },
    Loyal: { color: "var(--vb-primary-500)", description: "Reliable ~monthly cadence" },
    Promising: { color: "var(--vb-primary-300)", description: "Recent, not yet a monthly habit" },
    "Needs attention": { color: "var(--vb-neutral-400)", description: "Missed a cycle or two" },
    "At risk": { color: "var(--vb-warning-500)", description: "Was monthly, now overdue" },
    Hibernating: { color: "var(--vb-error-500)", description: "No send in 3+ cycles" },
};

const metaFor = (segment: string): SegmentMeta =>
    SEGMENT_META[segment] ?? { color: "var(--vb-neutral-400)", description: "" };

const ShareBar: React.FC<{ value: number; color: string }> = ({ value, color }) => (
    <div className="ci-bar">
        <div className="ci-bar__track">
            <div
                className="ci-bar__fill"
                style={{ width: `${Math.min(100, value)}%`, ["--ci-bar-color" as string]: color }}
            />
        </div>
        <span className="ci-bar__value">{formatters.formatPercentage(value)}</span>
    </div>
);

export const RfmSegmentsSection: React.FC<Props> = ({ segments }) => (
    <section className="section">
        <SectionHeader
            title="RFM segments"
            count={segments.length}
            meta="recency · frequency · monetary scoring per account"
        />
        <div className="card ci-table-card">
            {segments.length === 0 ? (
                <div className="ci-empty">No accounts in range.</div>
            ) : (
                <div className="ci-scroll">
                    <table className="ci-tbl">
                        <thead>
                            <tr>
                                <th scope="col">Segment</th>
                                <th scope="col" className="num">Accounts</th>
                                <th scope="col">% of accounts</th>
                                <th scope="col" className="num">Revenue</th>
                                <th scope="col">% of revenue</th>
                                <th scope="col" className="num">Avg recency</th>
                                <th scope="col" className="num">Sends / month</th>
                                <th scope="col" className="num">Avg revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {segments.map((segment) => {
                                const meta = metaFor(segment.segment);
                                return (
                                    <tr key={segment.segment}>
                                        <td>
                                            <span className="ci-seg">
                                                <span
                                                    className="ci-seg__dot"
                                                    style={{ ["--ci-dot" as string]: meta.color }}
                                                />
                                                {segment.segment}
                                            </span>
                                            {meta.description !== "" ? (
                                                <div
                                                    style={{
                                                        fontSize: 11,
                                                        color: "var(--vb-fg-muted)",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {meta.description}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="num">{formatters.formatNumber(segment.accounts)}</td>
                                        <td>
                                            <ShareBar value={segment.accountShare} color={meta.color} />
                                        </td>
                                        <td className="num">{formatters.formatCurrency(segment.revenue)}</td>
                                        <td>
                                            <ShareBar value={segment.revenueShare} color={meta.color} />
                                        </td>
                                        <td className="num">{segment.avgRecencyDays}d</td>
                                        <td className="num">{formatters.formatNumber(segment.avgOrdersPerMonth)}</td>
                                        <td className="num">{formatters.formatCurrency(segment.avgMonetary)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    </section>
);
